import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  apiFetch,
} from "../api/client";

import {
  tenantPath,
} from "../utils/tenantContext";

import "./TargetVisibilityPanel.css";


async function requestJson(
  path,
  options = {}
) {
  const response =
    await apiFetch(
      tenantPath(path),
      options
    );

  let body = {};

  try {
    body =
      await response.json();
  } catch (_) {
    // Empty response.
  }

  if (!response.ok) {
    throw new Error(
      body.detail ||
      `Request failed (${response.status})`
    );
  }

  return body;
}


function TargetVisibilityPanel() {
  const [devices, setDevices] =
    useState([]);

  const [sensors, setSensors] =
    useState([]);

  const [mappings, setMappings] =
    useState([]);

  const [deviceUuid, setDeviceUuid] =
    useState("");

  const [sensorId, setSensorId] =
    useState("");

  const [method, setMethod] =
    useState("unknown");

  const [status, setStatus] =
    useState("unverified");

  const [notes, setNotes] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");


  const load =
    useCallback(
      async () => {
        try {
          setError("");

          const [
            deviceData,
            sensorData,
            mappingData,
          ] = await Promise.all([
            requestJson(
              "/network-protection/pcap/targets/devices"
            ),

            requestJson(
              "/network-protection/pcap/targets/sensors"
            ),

            requestJson(
              "/network-protection/pcap/targets/mappings"
            ),
          ]);

          const nextDevices =
            Array.isArray(
              deviceData.devices
            )
              ? deviceData.devices
              : [];

          const nextSensors =
            Array.isArray(
              sensorData.sensors
            )
              ? sensorData.sensors
              : [];

          setDevices(nextDevices);
          setSensors(nextSensors);

          setMappings(
            Array.isArray(
              mappingData.mappings
            )
              ? mappingData.mappings
              : []
          );

          setDeviceUuid(
            (current) =>
              current ||
              nextDevices[0]?.device_uuid ||
              ""
          );

          setSensorId(
            (current) =>
              current ||
              (
                nextSensors[0]?.id
                  ? String(
                      nextSensors[0].id
                    )
                  : ""
              )
          );

        } catch (loadError) {
          setError(
            loadError.message
          );
        }
      },
      []
    );


  useEffect(() => {
    load();
  }, [load]);


  const selectedDevice =
    useMemo(
      () =>
        devices.find(
          (device) =>
            device.device_uuid ===
            deviceUuid
        ) || null,
      [
        devices,
        deviceUuid,
      ]
    );


  const selectedMapping =
    useMemo(
      () =>
        mappings.find(
          (mapping) =>
            mapping.device_uuid ===
              deviceUuid &&
            String(
              mapping.sensor_id
            ) === String(
              sensorId
            )
        ) || null,
      [
        mappings,
        deviceUuid,
        sensorId,
      ]
    );


  useEffect(() => {
    if (!selectedMapping) {
      setMethod("unknown");
      setStatus("unverified");
      setNotes("");
      return;
    }

    setMethod(
      selectedMapping.visibility_method ||
      "unknown"
    );

    setStatus(
      selectedMapping.visibility_status ||
      "unverified"
    );

    setNotes(
      selectedMapping.analyst_notes ||
      ""
    );
  }, [selectedMapping]);


  const saveMapping =
    async () => {
      if (
        !deviceUuid ||
        !sensorId
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          "Save this preliminary visibility assessment? VERIFIED status is available only through Observability Path Validation."
        );

      if (!confirmed) return;

      try {
        setBusy(true);
        setError("");

        await requestJson(
          "/network-protection/pcap/targets/mappings",
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              sensor_id:
                Number(sensorId),

              device_uuid:
                deviceUuid,

              visibility_method:
                method,

              visibility_status:
                status,

              analyst_notes:
                notes || null,
            }),
          }
        );

        await load();

      } catch (saveError) {
        setError(
          saveError.message
        );
      } finally {
        setBusy(false);
      }
    };


  return (
    <section className="targetVisibilityPanel">
      <div className="targetVisibilityHeader">
        <div>
          <span>
            TARGET VISIBILITY
          </span>

          <h3>
            Sensor-to-Device Mapping
          </h3>

          <p>
            Verify that the selected sensor
            can physically observe the target
            device before targeted packet
            capture is permitted.
          </p>
        </div>

        <div
          className={
            selectedMapping
              ?.visibility_status ===
              "verified"
              ? "targetVisibilityBadge verified"
              : selectedMapping
                  ?.visibility_status ===
                  "not_observable"
                ? "targetVisibilityBadge blocked"
                : "targetVisibilityBadge"
          }
        >
          {
            selectedMapping
              ?.visibility_status
              ?.replaceAll(
                "_",
                " "
              )
              .toUpperCase()
            ||
            "UNVERIFIED"
          }
        </div>
      </div>

      {error ? (
        <div className="targetVisibilityError">
          {error}
        </div>
      ) : null}

      <div className="targetVisibilityGrid">
        <label>
          Managed Device

          <select
            value={deviceUuid}
            disabled={busy}
            onChange={(event) =>
              setDeviceUuid(
                event.target.value
              )
            }
          >
            {devices.map(
              (device) => (
                <option
                  key={
                    device.device_uuid
                  }
                  value={
                    device.device_uuid
                  }
                >
                  {
                    device.hostname ||
                    device.device_uuid
                  }
                  {" · "}
                  {
                    device.platform ||
                    "unknown"
                  }
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Network Sensor

          <select
            value={sensorId}
            disabled={busy}
            onChange={(event) =>
              setSensorId(
                event.target.value
              )
            }
          >
            {sensors.map(
              (sensor) => (
                <option
                  key={sensor.id}
                  value={sensor.id}
                >
                  Sensor {sensor.id}
                  {" · "}
                  {
                    sensor.capture_interface ||
                    "interface unknown"
                  }
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Visibility Method

          <select
            value={method}
            disabled={busy}
            onChange={(event) =>
              setMethod(
                event.target.value
              )
            }
          >
            <option value="unknown">
              Unknown
            </option>

            <option value="local">
              Local Sensor
            </option>

            <option value="routed">
              Routed Through Sensor
            </option>

            <option value="mirror">
              SPAN / Mirror
            </option>

            <option value="tunnel">
              Authorized Tunnel
            </option>
          </select>
        </label>

        <label>
          Visibility State

          <select
            value={status}
            disabled={busy}
            onChange={(event) =>
              setStatus(
                event.target.value
              )
            }
          >
            <option value="unverified">
              Unverified
            </option>

            <option value="not_observable">
              Not Observable
            </option>
          </select>
        </label>
      </div>

      <label className="targetVisibilityNotes">
        Analyst Notes

        <textarea
          rows={3}
          value={notes}
          disabled={busy}
          onChange={(event) =>
            setNotes(
              event.target.value
            )
          }
          placeholder="Document why this target is observable, how traffic reaches the sensor, or why it is not observable."
        />
      </label>

      <div className="targetVisibilitySummary">
        <div>
          <span>
            Device
          </span>

          <strong>
            {
              selectedDevice
                ?.hostname ||
              "—"
            }
          </strong>
        </div>

        <div>
          <span>
            Platform
          </span>

          <strong>
            {
              selectedDevice
                ?.platform ||
              "—"
            }
          </strong>
        </div>

        <div>
          <span>
            Device State
          </span>

          <strong>
            {
              selectedDevice
                ?.status ||
              "—"
            }
          </strong>
        </div>

        <div>
          <span>
            Last Verified
          </span>

          <strong>
            {
              selectedMapping
                ?.verified_at
                ? new Date(
                    selectedMapping
                      .verified_at
                  ).toLocaleString()
                : "—"
            }
          </strong>
        </div>
      </div>

      <button
        type="button"
        className="targetVisibilitySave"
        disabled={
          busy ||
          !deviceUuid ||
          !sensorId
        }
        onClick={saveMapping}
      >
        Save Visibility Assessment
      </button>

      <div className="targetVisibilityBoundary">
        This mapping does not start capture.
        Targeted capture must remain blocked
        unless visibility is explicitly verified.
        No endpoint agent modification occurs
        in this step.
      </div>
    </section>
  );
}

export default TargetVisibilityPanel;
